/** Browser-safe first-idea request validation. No profile inference or provider work. */
import { parseContentContextInput, } from './content-context.js';
export const IDEA_ENTRY_SCHEMA_VERSION = 'idea-entry-intent.v1';
export const IDEA_ENTRY_POLICY_VERSION = 'idea-entry-policy.v1';
export const IDEA_ENTRY_TOPIC_SCHEMA_VERSION = 'idea-entry-intent.v2';
export const IDEA_ENTRY_TOPIC_POLICY_VERSION = 'idea-entry-policy.v2-topic';
export const IDEA_ENTRY_TEXT_MAX = 2000;
export const IDEA_ENTRY_INSTRUCTIONS_MAX = 4000;
export class IdeaEntryInputError extends Error {
    field;
    constructor(field) {
        super(`Invalid idea entry field: ${field}`);
        this.field = field;
        this.name = 'IdeaEntryInputError';
    }
}
const INPUT_KEYS = [
    'mode',
    'originalSeed',
    'optionalFocus',
    'instructions',
    'outputLocale',
    'context',
];
function text(value, field, maximum) {
    if (value === undefined || value === null)
        return null;
    if (typeof value !== 'string' || Array.from(value).length > maximum)
        throw new IdeaEntryInputError(field);
    // Preserve original bytes, newlines and formatting. Only test emptiness with trim.
    return value.trim().length === 0 ? null : value;
}
/** A client cannot choose count, approved context, provenance or effective focus. */
export function parseIdeaEntryInput(raw) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
        throw new IdeaEntryInputError('body');
    const input = raw;
    for (const key of Object.keys(input))
        if (!INPUT_KEYS.includes(key))
            throw new IdeaEntryInputError(key);
    if (input['outputLocale'] !== 'en' && input['outputLocale'] !== 'ar')
        throw new IdeaEntryInputError('outputLocale');
    const originalSeed = text(input['originalSeed'], 'originalSeed', IDEA_ENTRY_TEXT_MAX);
    const optionalFocus = text(input['optionalFocus'], 'optionalFocus', IDEA_ENTRY_TEXT_MAX);
    const instructions = text(input['instructions'], 'instructions', IDEA_ENTRY_INSTRUCTIONS_MAX);
    let context;
    if (Object.hasOwn(input, 'context')) {
        try {
            const parsed = parseContentContextInput(input['context']);
            if (parsed.kind !== 'INDEPENDENT_TOPIC')
                throw new IdeaEntryInputError('context');
            context = parsed;
        }
        catch {
            throw new IdeaEntryInputError('context');
        }
    }
    const shared = {
        instructions,
        outputLocale: input['outputLocale'],
        ...(context === undefined ? {} : { context }),
    };
    if (input['mode'] === 'DISCOVER_FOR_ME') {
        if (originalSeed !== null)
            throw new IdeaEntryInputError('originalSeed');
        return { ...shared, mode: 'DISCOVER_FOR_ME', originalSeed: null, optionalFocus };
    }
    if (input['mode'] === 'DEVELOP_MY_IDEA') {
        if (originalSeed === null)
            throw new IdeaEntryInputError('originalSeed');
        if (optionalFocus !== null)
            throw new IdeaEntryInputError('optionalFocus');
        return { ...shared, mode: 'DEVELOP_MY_IDEA', originalSeed, optionalFocus: null };
    }
    throw new IdeaEntryInputError('mode');
}
export function requestedIdeaCount(mode) {
    switch (mode) {
        case 'DISCOVER_FOR_ME':
            return 3;
        case 'DEVELOP_MY_IDEA':
            return 1;
        default:
            throw new IdeaEntryInputError('mode');
    }
}
/** Never substitute UI/output locale for the language of the original text. */
export function observeIdeaSeedLanguage(seed) {
    if (seed === null)
        return 'unknown';
    const letters = seed.match(/\p{Letter}/gu) ?? [];
    if (letters.length === 0)
        return 'unknown';
    const arabic = letters.some((letter) => /\p{Script=Arabic}/u.test(letter));
    const latin = letters.some((letter) => /[A-Za-z]/u.test(letter));
    const other = letters.some((letter) => !/[A-Za-z\p{Script=Arabic}]/u.test(letter));
    if (other)
        return 'unknown';
    if (arabic && latin)
        return 'mixed';
    if (arabic)
        return 'ar';
    return latin ? 'en' : 'unknown';
}
//# sourceMappingURL=idea-entry.js.map